import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { getMessage } from "../data/locales";
import { isTestLinkedInAnyTemario, linkTestToTemarioPath } from "../utils/linkTestToTemarioPath";
import { listPathOptions } from "../utils/temarioTree";
import { TEMARIO_DEPTH_LABELS } from "../pages/Enlaces_Administracion/Acciones_gc/Cursos_Creacion/editarCursoConstants";
import ViewportSelect from "./ViewportSelect";

const testShape = PropTypes.shape({
  titulo: PropTypes.string,
  pestanas: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.string), PropTypes.string]),
});

function preferredTestTab(test) {
  if (!test || typeof test !== "object") {
    return "";
  }
  if (Array.isArray(test.pestanas) && test.pestanas.length > 0) {
    return String(test.pestanas[0] ?? "").trim();
  }
  if (typeof test.pestanas === "string") {
    return test.pestanas.trim();
  }
  return "";
}

function encodeLinkTarget(tab, pathKey) {
  return JSON.stringify({ tab, pathKey });
}

function decodeLinkTarget(value) {
  if (typeof value !== "string" || value === "") {
    return null;
  }
  try {
    const parsed = JSON.parse(value);
    if (!parsed || typeof parsed !== "object") {
      return null;
    }
    const tab = typeof parsed.tab === "string" ? parsed.tab : "";
    const pathKey = typeof parsed.pathKey === "string" ? parsed.pathKey : "";
    if (!tab) {
      return null;
    }
    return { tab, pathKey };
  } catch {
    return null;
  }
}

function collectTemarioLinkOptions(bloquesContenido, preferredTab) {
  const src = bloquesContenido && typeof bloquesContenido === "object" ? bloquesContenido : {};
  const tabs = Object.keys(src);
  const ordered = preferredTab && tabs.includes(preferredTab)
    ? [preferredTab, ...tabs.filter((tab) => tab !== preferredTab)]
    : tabs;
  /** @type {Array<{ value: string, label: string }>} */
  const options = [];
  for (const tab of ordered) {
    const list = Array.isArray(src[tab]) ? src[tab] : [];
    const paths = listPathOptions(list, TEMARIO_DEPTH_LABELS);
    for (const opt of paths) {
      options.push({
        value: encodeLinkTarget(tab, opt.pathKey),
        label: tab === preferredTab ? opt.label : `${tab} · ${opt.label}`,
      });
    }
  }
  return options;
}

export default function CourseTestTemarioLinker({
  test,
  bloquesContenido,
  setBloquesContenido,
  setMensaje,
  disabled,
}) {
  const [targetValue, setTargetValue] = useState("");
  const preferredTab = preferredTestTab(test);
  const pathOptions = useMemo(
    () => collectTemarioLinkOptions(bloquesContenido, preferredTab),
    [bloquesContenido, preferredTab],
  );

  const titulo = typeof test?.titulo === "string" ? test.titulo.trim() : "";
  const alreadyLinked = isTestLinkedInAnyTemario(bloquesContenido, titulo);

  if (!titulo) {
    return null;
  }

  const handleLink = () => {
    if (disabled) {
      return;
    }
    const target = decodeLinkTarget(targetValue);
    if (!target) {
      setMensaje(`⚠️ ${getMessage("courseEditor.testsTemario.linkInvalidPath")}`);
      return;
    }
    const result = linkTestToTemarioPath(bloquesContenido, target.tab, target.pathKey, {
      titulo,
      bloque: target.tab,
    });
    if (!result.ok) {
      const msgKey = {
        missing_pestana: "courseEditor.testsTemario.linkMissingPestana",
        invalid_path: "courseEditor.testsTemario.linkInvalidPath",
        already_linked: "courseEditor.testsTemario.linkAlready",
      }[result.error];
      setMensaje(`⚠️ ${getMessage(msgKey ?? "courseEditor.testsTemario.linkInvalidPath")}`);
      return;
    }
    setBloquesContenido(result.next);
    setTargetValue("");
    setMensaje(`✅ ${getMessage("courseEditor.testsTemario.linkSuccess")}`);
  };

  return (
    <div className="course-test-temario-linker">
      {alreadyLinked ? (
        <span className="course-test-temario-linker__badge">
          {getMessage("courseEditor.testsTemario.linkedBadge")}
        </span>
      ) : null}
      <ViewportSelect
        className="form-control course-test-temario-linker__select"
        value={targetValue}
        disabled={disabled || pathOptions.length === 0}
        onChange={(e) => setTargetValue(e.target.value)}
        aria-label={getMessage("courseEditor.testsTemario.linkSelectPlaceholder")}
      >
        <option value="">{getMessage("courseEditor.testsTemario.linkSelectPlaceholder")}</option>
        {pathOptions.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </ViewportSelect>
      <button
        type="button"
        className="style2-button course-test-temario-linker__btn"
        disabled={disabled || !targetValue}
        onClick={handleLink}
      >
        {getMessage("courseEditor.testsTemario.linkButton")}
      </button>
    </div>
  );
}

CourseTestTemarioLinker.propTypes = {
  test: testShape.isRequired,
  bloquesContenido: PropTypes.objectOf(PropTypes.array).isRequired,
  setBloquesContenido: PropTypes.func.isRequired,
  setMensaje: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

CourseTestTemarioLinker.defaultProps = {
  disabled: false,
};
